import EligibilityPage from './pages/eligibility/EligibilityPage';
import { VisaDocumentsPage } from './pages/visa-documents/VisaDocumentsPage';
import { AgencyDocumentsPage } from './pages/agency-documents/AgencyDocumentsPage';
import { VisaSupportPage } from './pages/applications/VisaSupportPage';
import { CourseSearchPage, CourseDetailPage } from '@/pages/courses/CourseSearchPage';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { AuthProvider } from '@/hooks/useAuth';
import { SettingsProvider, useSettings, Settings } from '@/contexts/SettingsContext';
import { Favicon } from '@/components/Favicon';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { MultiStepRegisterPage } from '@/pages/auth/MultiStepRegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ApplicationsListPage } from '@/pages/applications/ApplicationsListPage';
import { ApplicationFormPage } from '@/pages/applications/ApplicationFormPage';
import { ApplicationViewPage } from '@/pages/applications/ApplicationViewPage';
import { TermsAndConditionsPage } from '@/pages/applications/TermsAndConditionsPage';
import { CommissionsListPage } from '@/pages/commissions/CommissionsListPage';
import { CommissionDetailPage } from '@/pages/commissions/CommissionDetailPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { PartnershipsPage } from '@/pages/partnerships/PartnershipsPage';
import { SubAccountsPage } from '@/pages/sub-accounts/SubAccountsPage';
import { StudentProfilesPage } from '@/pages/student-profiles/StudentProfilesPage';
import { StudentProfileDetailPage } from '@/pages/student-profiles/StudentProfileDetailPage';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';

interface AppProps {
  initialSettings?: Settings | null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const { getTitle } = useSettings();
  const title = getTitle();

  return (
    <>
      <Favicon />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Helmet>
          <title>{title} Agent Portal</title>
        </Helmet>
        
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<MultiStepRegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          
          {/* Protected Routes - Terms page accessible without layout */}
          <Route path="/applications/terms" element={
            <ProtectedRoute>
              <TermsAndConditionsPage />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="visa-documents" element={<VisaDocumentsPage />} />
            <Route path="applications" element={<ApplicationsListPage />} />
            <Route path="eligibility" element={<EligibilityPage />} />
            <Route path="eligibility/checks/:checkId" element={<EligibilityPage />} />
            <Route path="courses" element={<CourseSearchPage />} />
            <Route path="courses/:offeringId" element={<CourseDetailPage />} />
            <Route path="applications/new" element={<ApplicationFormPage />} />
            <Route path="applications/:id" element={<ApplicationViewPage />} />
            <Route path="applications/:id/visa-support" element={<VisaSupportPage />} />
            <Route path="applications/:id/edit" element={<ApplicationFormPage />} />
            <Route path="student-profiles" element={<StudentProfilesPage />} />
            <Route path="student-profiles/:id" element={<StudentProfileDetailPage />} />
            <Route path="commissions" element={<CommissionsListPage />} />
            <Route path="commissions/:id" element={<CommissionDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="profile/documents" element={<AgencyDocumentsPage />} />
            <Route path="partnerships" element={<PartnershipsPage />} />
            <Route path="sub-accounts" element={<SubAccountsPage />} />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <CookieConsentBanner />
      </Router>
    </>
  );
}

function App({ initialSettings = null }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider initialSettings={initialSettings}>
          <AppContent />
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
